import { z } from 'zod';
export const ModelProviderSchema = z.enum([
    'ANTHROPIC',
    'OPENAI',
    'AZURE_OPENAI',
    'BEDROCK',
    'VERTEX',
    'OLLAMA',
    'OPENAI_COMPATIBLE',
    'GOOGLE',
]);
/**
 * Body for `POST /v1/model-configs/:id/resolve` (internal service callers).
 * `envProviders` = providers the caller holds a WORKING pod-level env
 * credential for (probed, not just present). When supplied and the requested
 * config can't run for the caller (no stored creds + provider not in the
 * list), the API falls back to the newest org/global config with stored
 * creds. Omitted = legacy behavior, no runnability filtering.
 */
export const ResolveModelConfigInputSchema = z.object({
    envProviders: z.array(ModelProviderSchema).max(16).optional(),
});
/**
 * Generation (sampling) parameters that live inside a ModelConfig's `config`
 * blob. These are the provider-agnostic knobs the runtime threads into every
 * LLM call the config drives (chat, persistent agents, workflow nodes, evals)
 * via `defaultSettingsMiddleware` in `@cortex/mastra-layer`.
 *
 * All optional — omit to keep the provider/model default. Stored inside the
 * free-form `config` record (no dedicated columns), so this schema is a typed
 * VIEW over the known keys and `.passthrough()` preserves any provider-specific
 * extras already stored there (backwards-compatible superset of the old
 * `z.record(z.string(), z.unknown())`).
 *
 * ── Anthropic + extended thinking ──────────────────────────────────────────
 * Anthropic's API rejects `temperature`/`topP` other than the default whenever
 * extended thinking is active — and on models like claude-sonnet-5 adaptive
 * thinking is ON by default. The mastra-layer applier therefore only forwards
 * `temperature`/`topP` for Anthropic when thinking is EXPLICITLY disabled;
 * `maxOutputTokens` is always safe. See `modelCallSettings` there.
 *
 * `maxOutputTokens` is the ai-sdk v5 key (NOT the legacy `maxTokens`).
 *
 * ── `temperature` range is provider-dependent ──────────────────────────────
 * The bound below is 0–2 (the OpenAI / OpenAI-compatible range). Anthropic's
 * max is 1 — a value like 1.5 on an Anthropic config (thinking off) draws a
 * provider 400. We intentionally do NOT provider-aware-clamp here (the same
 * schema serves OpenAI-compatible endpoints, which is what Intel runs); the
 * 0–2 bound stays and the console field help text warns that Anthropic caps at
 * 1.
 */
export const ModelParamsSchema = z
    .object({
    /**
     * Sampling temperature. 0 = deterministic, higher = more random. Range is
     * provider-dependent: 0–2 for OpenAI-compatible endpoints, but Anthropic
     * caps at 1 (a higher value 400s on Anthropic). The bound here is the
     * permissive OpenAI range — not provider-clamped.
     */
    temperature: z.number().min(0).max(2).optional(),
    /** Hard cap on tokens generated per response (ai-sdk v5 `maxOutputTokens`). */
    maxOutputTokens: z.number().int().positive().max(200_000).optional(),
    /** Nucleus sampling — consider tokens within the top `topP` probability mass. */
    topP: z.number().min(0).max(1).optional(),
    /**
     * Model-call inactivity deadline (seconds) enforced by the persistent-agent
     * host's turn watchdog: a call that streams nothing for this long is
     * aborted and the turn fails loudly instead of hanging the wake loop
     * forever (campaign bug: an unresponsive provider silenced a supervisor
     * for a full mission). Default 180s when unset. Host-enforced — NOT
     * forwarded to the provider as a sampling setting.
     */
    callTimeoutSec: z.number().int().min(10).max(3600).optional(),
})
    .passthrough();
export const ModelConfigSchema = z.object({
    id: z.string(),
    /** `null` for Formant global defaults (seed-managed rows such as `mc_system_brief`). */
    orgId: z.string().nullable(),
    name: z.string(),
    provider: ModelProviderSchema,
    modelId: z.string(),
    endpoint: z.string().nullable(),
    config: ModelParamsSchema,
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * Query for `GET /v1/model-configs`. `includeGlobal=true` also returns the
 * Formant global defaults (orgId null) that managed workflow templates
 * reference, so the model picker can show them. (DEV-17471.)
 */
export const ListModelConfigsQuerySchema = z
    .object({ includeGlobal: z.enum(['true', 'false']).optional() })
    .strict();
export const CreateModelConfigInputSchema = z.object({
    name: z.string().min(1).max(200),
    provider: ModelProviderSchema,
    modelId: z.string().min(1),
    endpoint: z.string().url().optional(),
    /** Plaintext credential at the API boundary — api encrypts before storing. */
    creds: z.string().optional(),
    /** Back-compat alias. Prefer `creds`. */
    credsEncrypted: z.string().optional(),
    /** Generation params (temperature, maxOutputTokens, topP) + any extras. */
    config: ModelParamsSchema.optional(),
});
export const UpdateModelConfigInputSchema = CreateModelConfigInputSchema.partial();
/**
 * Body for the "test image" endpoint — run a multimodal completion against an
 * image. `imageUrl` accepts either an http(s) URL or a base64 data URL
 * (`data:image/...;base64,...`) for drag-and-drop uploads.
 */
export const TestModelImageInputSchema = z.object({
    imageUrl: z
        .string()
        .min(1)
        .refine((v) => /^https?:\/\//.test(v) || /^data:image\/[a-z+.-]+;base64,/.test(v), {
        message: 'imageUrl must be an http(s) URL or a base64 image data URL',
    }),
    prompt: z.string().min(1).max(8000),
});
/**
 * Body for the "test video" endpoint. `videoUrl` accepts an http(s) URL or a
 * base64 data URL (`data:video/...;base64,...`) for a dropped file.
 */
export const TestModelVideoInputSchema = z.object({
    videoUrl: z
        .string()
        .min(1)
        .refine((v) => /^https?:\/\//.test(v) || /^data:video\/[a-z0-9.+-]+;base64,/i.test(v), {
        message: 'videoUrl must be an http(s) URL or a base64 video data URL',
    }),
    prompt: z.string().min(1).max(8000),
});
//# sourceMappingURL=model.js.map